/**
 * API Configuration
 * Centralized API base URL configuration for all services
 *
 * This file provides a single source of truth for API endpoints,
 * eliminating inconsistencies across different services.
 */

/**
 * Get the API base URL based on environment
 * Priority:
 * 1. VITE_API_BASE_URL environment variable
 * 2. Auto-detect based on NODE_ENV
 * 3. Default to localhost
 */
const getApiBaseUrl = () => {
  // If explicitly set via environment variable, use it
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL
  }

  // Auto-detect based on environment
  const nodeEnv = import.meta.env.MODE || import.meta.env.NODE_ENV || 'development'

  switch (nodeEnv) {
    case 'production':
      // Production API URL
      return 'https://api.hocon-techindia.in'

    case 'staging':
      // Staging API URL (if you have one)
      return 'https://api-staging.hocon-techindia.in'

    case 'development':
    case 'local':
    default:
      // Local development API URL
      return 'http://localhost:3003'
  }
}

/**
 * API Base URL
 * Use this constant in all services instead of hardcoding URLs
 */
export const API_BASE_URL = getApiBaseUrl()

/**
 * API Endpoints
 * Centralized endpoint definitions
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    CONFIG: `${API_BASE_URL}/api/auth/config`,
    AUTHORIZE: `${API_BASE_URL}/api/auth/authorize`,
    CALLBACK: `${API_BASE_URL}/api/auth/callback`,
    REFRESH: `${API_BASE_URL}/api/auth/refresh`,
    PROFILE: `${API_BASE_URL}/api/auth/profile`,
    STATUS: `${API_BASE_URL}/api/auth/status`,
    LOGOUT: `${API_BASE_URL}/api/auth/logout`,
    USER_ROLE: `${API_BASE_URL}/api/auth/user-role`,
    TOKEN_INFO: `${API_BASE_URL}/api/auth/token-info`,
    USER_PROJECTS: `${API_BASE_URL}/api/auth/user/projects`,
  },

  // Form Permissions (using auth/user-role endpoint)
  FORM_PERMISSIONS: {
    CHECK_ACCESS: (projectId) => `${API_BASE_URL}/api/auth/user-role?projectId=${projectId}`,
    TEMPLATE_PERMISSIONS: (projectId, templateId) =>
      `${API_BASE_URL}/api/form-permissions/${projectId}/templates/${templateId}`,
    USER_GROUPS: (projectId, userId = null) =>
      `${API_BASE_URL}/api/form-permissions/${projectId}/user-groups${userId ? `?userId=${userId}` : ''}`,
  },

  // Projects
  PROJECTS: {
    BASE: `${API_BASE_URL}/api/projects`,
    LOCATIONS: (projectId) => `${API_BASE_URL}/api/projects/${projectId}/locations`,
    ASSETS: (projectId) => `${API_BASE_URL}/api/projects/${projectId}/assets`,
  },

  // Field Reports
  FIELD_REPORTS: {
    BASE: `${API_BASE_URL}/api/field-reports`,
    BY_PROJECT: (projectId) => `${API_BASE_URL}/api/field-reports/${projectId}`,
    TEMPLATE: (projectId, templateId) => `${API_BASE_URL}/api/field-reports/${projectId}/template/${templateId}`,
  },

  // Config
  CONFIG: `${API_BASE_URL}/api/config`,

  // Reviewers
  REVIEWERS: `${API_BASE_URL}/api/reviewers`,

  // Assets
  ASSETS: `${API_BASE_URL}/api/assets`,

  // Users
  USERS: `${API_BASE_URL}/api/users`,

  // Companies
  COMPANIES: `${API_BASE_URL}/api/companies`,

  // Vehicles
  VEHICLES: `${API_BASE_URL}/api/vehicles`,

  // Charge Out
  CHARGE_OUT: `${API_BASE_URL}/api/charge-out`,

  // Cost Management
  COST: `${API_BASE_URL}/api/cost`,
}

/**
 * Environment information (for debugging)
 */
export const ENV_INFO = {
  mode: import.meta.env.MODE,
  nodeEnv: import.meta.env.NODE_ENV,
  apiBaseUrl: API_BASE_URL,
  isProduction: import.meta.env.PROD,
  isDevelopment: import.meta.env.DEV,
}

/**
 * Log configuration on startup (only in development)
 */

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  ENV_INFO,
}
